import { ChangeDetectionStrategy, Component, computed, effect, input, output, signal } from '@angular/core';

import { UiAutocompleteFieldComponent } from '../ui-autocomplete-field/ui-autocomplete-field.component';
import { UiAutocompleteOption } from '../ui-autocomplete-field/ui-autocomplete-field.types';
import { UiButtonComponent } from '../ui-button/ui-button.component';
import { UiFormFieldComponent } from '../ui-form-field/ui-form-field.component';
import { UiLabelTranslatePipe } from '../ui-label-translate.pipe';
import { UiSelectComponent } from '../ui-select/ui-select.component';
import { UiSelectOption } from '../ui-select/ui-select.types';
import { UiToggleComponent } from '../ui-toggle/ui-toggle.component';
import {
  UiTableAction,
  UiTableActionClick,
  UiTableBadgeRule,
  UiTableBadgeTone,
  UiTableCellClick,
  UiTableColumn,
  UiTableDetailField,
  UiTableDetailMode,
  UiTableDetailSection,
  UiTableDetailTab,
  UiTableFilterField,
  UiTableFirstColumnHoverMode,
  UiTableSelectionChange,
  UiTableSortChange,
  UiTableSortOrder,
  UiTableToggleChange
} from './ui-table.types';

type UiTableDrawerType = 'none' | 'filter' | 'detail';

@Component({
  selector: 'ui-table',
  standalone: true,
  imports: [UiAutocompleteFieldComponent, UiButtonComponent, UiFormFieldComponent, UiLabelTranslatePipe, UiSelectComponent, UiToggleComponent],
  templateUrl: './ui-table.component.html',
  styleUrl: './ui-table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UiTableComponent {
  private static readonly SUPPORTED_PAGE_SIZE_OPTIONS = [10, 20, 50, 100] as const;

  readonly columns = input<UiTableColumn[]>([]);
  readonly data = input<unknown[]>([]);
  readonly ariaLabel = input('Data table');
  readonly emptyMessage = input('No records found.');

  readonly showToolbar = input(true);
  readonly showSearch = input(true);
  readonly showSort = input(true);
  readonly showFilter = input(true);
  readonly showPagination = input(false);
  readonly defaultPageSize = input(10);
  readonly pageSizeOptions = input<number[]>([10, 20, 50, 100]);
  readonly showPageSizeSelector = input(true);
  readonly showSelection = input(false);
  readonly selectionKey = input('id');
  readonly showBulkDeleteButton = input(false);
  readonly bulkDeleteLabel = input('Delete All');
  readonly showActionsColumn = input(false);
  readonly actionsColumnLabel = input('Actions');
  readonly rowActions = input<UiTableAction[]>([
    { id: 'edit', label: 'Edit', variant: 'ghost' },
    { id: 'delete', label: 'Delete', variant: 'danger' }
  ]);
  readonly searchPlaceholder = input('Search...');
  readonly searchKeys = input<string[]>([]);
  readonly firstColumnHover = input<UiTableFirstColumnHoverMode>('hover');
  readonly badgeRules = input<Record<string, UiTableBadgeRule>>({
    active: { tone: 'green' },
    ok: { tone: 'green' },
    done: { tone: 'green' },
    inactive: { tone: 'neutral' },
    low: { tone: 'yellow' },
    medium: { tone: 'yellow' },
    pending: { tone: 'yellow' },
    'out-of-stock': { tone: 'red' },
    overdue: { tone: 'red' },
    high: { tone: 'red' },
    field: { tone: 'yellow' },
    'field-agent': { tone: 'yellow' },
    agency: { tone: 'green' },
    'agency-agent': { tone: 'green' }
  });

  readonly defaultSortKey = input('');
  readonly defaultSortOrder = input<UiTableSortOrder>('asc');

  readonly filterFields = input<UiTableFilterField[]>([]);

  readonly rowClickable = input(false);
  readonly detailTitleKey = input('name');
  readonly detailFields = input<UiTableDetailField[]>([]);
  readonly detailTabs = input<UiTableDetailTab[]>([]);
  readonly detailMode = input<UiTableDetailMode>('side-panel');

  readonly cellClick = output<UiTableCellClick>();
  readonly rowClick = output<unknown>();
  readonly actionClick = output<UiTableActionClick>();
  readonly toggleChange = output<UiTableToggleChange>();
  readonly sortChange = output<UiTableSortChange>();
  readonly selectionChange = output<UiTableSelectionChange>();
  readonly bulkDelete = output<unknown[]>();

  readonly searchQuery = signal('');
  readonly sortKey = signal('');
  readonly sortOrder = signal<UiTableSortOrder>('asc');
  readonly selectedRow = signal<unknown | null>(null);
  readonly selectedRowKeys = signal<string[]>([]);
  readonly pageIndex = signal(0);
  readonly pageSize = signal(10);
  readonly activeDrawer = signal<UiTableDrawerType>('none');
  readonly activeDetailTabId = signal('');

  readonly filterDraft = signal<Record<string, string | boolean>>({});
  readonly filterApplied = signal<Record<string, string | boolean>>({});
  readonly appliedFilterCount = computed(() => {
    const state = this.filterApplied();
    let count = 0;

    for (const value of Object.values(state)) {
      if (typeof value === 'boolean') {
        if (value) {
          count += 1;
        }

        continue;
      }

      if (String(value).trim() !== '') {
        count += 1;
      }
    }

    return count;
  });

  readonly sortableColumns = computed(() => this.columns().filter((column) => column.sortable));

  readonly processedRows = computed(() => {
    const query = this.searchQuery().trim().toLowerCase();
    const applied = this.filterApplied();

    const searched = this.data().filter((row) => {
      const matchesSearch = !query || this.matchesSearch(row, query);
      const matchesFilter = this.matchesAllFilters(row, applied);
      return matchesSearch && matchesFilter;
    });

    const sortKey = this.activeSortKey();

    if (!sortKey) {
      return searched;
    }

    const order = this.sortOrder();

    return [...searched].sort((leftRow, rightRow) => {
      const left = this.valueOf(leftRow, sortKey);
      const right = this.valueOf(rightRow, sortKey);
      const result = String(left ?? '').localeCompare(String(right ?? ''), undefined, { numeric: true });
      return order === 'asc' ? result : -result;
    });
  });

  readonly pagedRows = computed(() => {
    if (!this.showPagination()) {
      return this.processedRows();
    }

    const start = this.pageIndex() * this.pageSize();
    return this.processedRows().slice(start, start + this.pageSize());
  });

  readonly pageSizeSelectOptions = computed<UiSelectOption[]>(() =>
    this.resolvedPageSizeOptions().map((value) => ({ label: String(value), value: String(value) }))
  );
  readonly pageSizeValue = computed(() => `${this.pageSize()}`);

  readonly pageMeta = computed(() => {
    const total = this.processedRows().length;

    if (total === 0) {
      return { total: 0, start: 0, end: 0 };
    }

    if (!this.showPagination()) {
      return { total, start: 1, end: total };
    }

    const start = this.pageIndex() * this.pageSize() + 1;
    const end = Math.min(total, this.pageIndex() * this.pageSize() + this.pageSize());
    return { total, start, end };
  });

  readonly totalPages = computed(() => {
    if (!this.showPagination()) {
      return 1;
    }

    const total = this.processedRows().length;
    return Math.max(1, Math.ceil(total / this.pageSize()));
  });

  readonly selectedRowTitle = computed(() => {
    const row = this.selectedRow();

    if (!row) {
      return '';
    }

    const value = this.valueOf(row, this.detailTitleKey());
    return value === null || value === undefined ? '' : String(value);
  });

  readonly resolvedDetailTabs = computed<UiTableDetailTab[]>(() => {
    const tabs = this.detailTabs();

    if (tabs.length > 0) {
      return tabs;
    }

    if (this.detailFields().length === 0) {
      return [];
    }

    return [
      {
        id: 'overview',
        label: 'Overview',
        fields: this.detailFields()
      }
    ];
  });

  readonly activeDetailTab = computed(() => {
    const tabs = this.resolvedDetailTabs();

    if (tabs.length === 0) {
      return null;
    }

    const active = this.activeDetailTabId();
    return tabs.find((tab) => tab.id === active) ?? tabs[0];
  });

  readonly hasDetail = computed(() => this.resolvedDetailTabs().length > 0);
  readonly isRowClickable = computed(() => this.rowClickable());
  readonly displayedRows = computed(() => this.pagedRows());
  readonly firstDataColumnKey = computed(() => this.columns()[0]?.key ?? '');
  readonly isFilterDrawerOpen = computed(() => this.activeDrawer() === 'filter');
  readonly isDetailDrawerOpen = computed(() => this.activeDrawer() === 'detail');
  readonly isAnyDrawerOpen = computed(() => this.activeDrawer() !== 'none');

  readonly selectedRows = computed(() => {
    const selected = new Set(this.selectedRowKeys());
    return this.data().filter((row) => selected.has(this.rowKey(row)));
  });

  readonly allDisplayedSelected = computed(() => {
    const displayed = this.displayedRows();

    if (displayed.length === 0) {
      return false;
    }

    const selected = new Set(this.selectedRowKeys());
    return displayed.every((row) => selected.has(this.rowKey(row)));
  });

  constructor() {
    const initialSortKey = this.defaultSortKey();
    const fallbackSortKey = this.sortableColumns()[0]?.key ?? '';
    const resolvedSortKey = initialSortKey || fallbackSortKey;

    this.sortKey.set(resolvedSortKey);
    this.sortOrder.set(this.defaultSortOrder());
    this.pageSize.set(10);

    effect(() => {
      const availablePageSizes = this.resolvedPageSizeOptions();
      const currentPageSize = this.pageSize();
      const resolvedPageSize = availablePageSizes.includes(currentPageSize)
        ? currentPageSize
        : this.resolveInitialPageSize();

      if (currentPageSize !== resolvedPageSize) {
        this.pageSize.set(resolvedPageSize);
        this.pageIndex.set(0);
      }
    });
  }

  onSearchChange(value: string): void {
    this.searchQuery.set(value);
    this.pageIndex.set(0);
  }

  openFilterPanel(): void {
    this.filterDraft.set({ ...this.filterApplied() });
    this.selectedRow.set(null);
    this.activeDrawer.set('filter');
  }

  closeFilterPanel(): void {
    if (this.activeDrawer() === 'filter') {
      this.activeDrawer.set('none');
    }
  }

  openDetailPanel(row: unknown): void {
    if (!this.hasDetail() || this.detailMode() === 'none') {
      return;
    }

    this.selectedRow.set(row);

    const firstTab = this.resolvedDetailTabs()[0];
    this.activeDetailTabId.set(firstTab?.id ?? '');
    this.activeDrawer.set('detail');
  }

  closeDetailPanel(): void {
    if (this.activeDrawer() === 'detail') {
      this.activeDrawer.set('none');
    }

    this.selectedRow.set(null);
  }

  closeActiveDrawer(): void {
    const current = this.activeDrawer();

    if (current === 'filter') {
      this.closeFilterPanel();
      return;
    }

    if (current === 'detail') {
      this.closeDetailPanel();
    }
  }

  selectDetailTab(tabId: string): void {
    this.activeDetailTabId.set(tabId);
  }

  setFilterDraftText(fieldKey: string, value: string): void {
    this.filterDraft.update((state) => ({ ...state, [fieldKey]: value }));
  }

  setFilterDraftToggle(fieldKey: string, value: boolean): void {
    this.filterDraft.update((state) => ({ ...state, [fieldKey]: value }));
  }

  applyFilters(): void {
    this.filterApplied.set({ ...this.filterDraft() });
    this.pageIndex.set(0);
    this.closeFilterPanel();
  }

  resetFilters(): void {
    this.filterDraft.set({});
    this.filterApplied.set({});
    this.pageIndex.set(0);
    this.closeFilterPanel();
  }

  toggleColumnSort(column: UiTableColumn): void {
    if (!column.sortable) {
      return;
    }

    if (this.sortKey() === column.key) {
      this.sortOrder.set(this.sortOrder() === 'asc' ? 'desc' : 'asc');
    } else {
      this.sortKey.set(column.key);
      this.sortOrder.set('asc');
    }

    this.sortChange.emit({ key: this.sortKey(), order: this.sortOrder() });
    this.pageIndex.set(0);
  }

  sortIconFor(column: UiTableColumn): string {
    if (!column.sortable) {
      return '';
    }

    if (this.sortKey() !== column.key) {
      return '▾';
    }

    return this.sortOrder() === 'asc' ? '▴' : '▾';
  }

  badgeToneFor(raw: string): UiTableBadgeTone {
    const rule = this.badgeRules()[this.normalizeBadgeKey(raw)];
    return rule?.tone ?? 'neutral';
  }

  badgeBgFor(raw: string): string | null {
    const rule = this.badgeRules()[this.normalizeBadgeKey(raw)];
    return rule?.colors ? `var(${rule.colors.backgroundVar})` : null;
  }

  badgeTextFor(raw: string): string | null {
    const rule = this.badgeRules()[this.normalizeBadgeKey(raw)];
    return rule?.colors ? `var(${rule.colors.textVar})` : null;
  }

  onRowClick(event: Event, row: unknown): void {
    const target = event.target as HTMLElement | null;

    if (target && this.isInteractiveTarget(target)) {
      return;
    }

    if (this.isRowClickable() && this.hasDetail() && this.detailMode() === 'side-panel') {
      this.openDetailPanel(row);
    }

    this.rowClick.emit(row);
  }

  toggleAllDisplayed(checked: boolean): void {
    const selected = new Set(this.selectedRowKeys());

    if (checked) {
      this.displayedRows().forEach((row) => selected.add(this.rowKey(row)));
    } else {
      this.displayedRows().forEach((row) => selected.delete(this.rowKey(row)));
    }

    this.selectedRowKeys.set([...selected]);
    this.selectionChange.emit({ selectedRows: this.selectedRows() });
  }

  toggleRowSelection(event: Event, row: unknown, checked: boolean): void {
    event.stopPropagation();

    const key = this.rowKey(row);
    const selected = new Set(this.selectedRowKeys());

    if (checked) {
      selected.add(key);
    } else {
      selected.delete(key);
    }

    this.selectedRowKeys.set([...selected]);
    this.selectionChange.emit({ selectedRows: this.selectedRows() });
  }

  isRowSelected(row: unknown): boolean {
    return this.selectedRowKeys().includes(this.rowKey(row));
  }

  triggerBulkDelete(): void {
    this.bulkDelete.emit(this.selectedRows());
  }

  setPageSize(value: string): void {
    const parsed = Number(value);
    const supportedSizes = this.resolvedPageSizeOptions();

    if (!Number.isFinite(parsed) || !supportedSizes.includes(parsed)) {
      return;
    }

    this.pageSize.set(parsed);
    this.pageIndex.set(0);
  }

  previousPage(): void {
    const next = this.pageIndex() - 1;
    this.pageIndex.set(next < 0 ? 0 : next);
  }

  nextPage(): void {
    const max = this.totalPages() - 1;
    const next = this.pageIndex() + 1;
    this.pageIndex.set(next > max ? max : next);
  }

  canGoPrevious(): boolean {
    return this.pageIndex() > 0;
  }

  canGoNext(): boolean {
    return this.pageIndex() < this.totalPages() - 1;
  }

  onCellClick(event: Event, column: UiTableColumn, row: unknown): void {
    event.stopPropagation();

    if (!column.clickable) {
      return;
    }

    this.cellClick.emit({ key: column.key, row });

    if (this.hasDetail() && this.detailMode() === 'side-panel') {
      this.openDetailPanel(row);
    }
  }

  onActionClick(event: Event, actionId: string, row: unknown): void {
    event.stopPropagation();
    this.actionClick.emit({ actionId, row });
  }

  onToggleChange(column: UiTableColumn, row: unknown, checked: boolean): void {
    this.toggleChange.emit({ key: column.key, row, checked });
  }

  isIconOnlyAction(action: UiTableAction): boolean {
    return this.actionIconKind(action) !== null;
  }

  actionIconPath(action: UiTableAction): string {
    const kind = this.actionIconKind(action);

    if (kind === 'edit') {
      return 'M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zm2.92 2.33H5v-.92l9.06-9.06.92.92-9.06 9.06zM20.71 7.04a1.003 1.003 0 0 0 0-1.42L18.37 3.29a1.003 1.003 0 0 0-1.42 0l-1.83 1.83 3.75 3.75 1.84-1.83z';
    }

    if (kind === 'delete') {
      return 'M6 7h12l-1 14H7L6 7zm3-3h6l1 2h4v2H4V6h4l1-2z';
    }

    return '';
  }

  private actionIconKind(action: UiTableAction): 'edit' | 'delete' | null {
    const key = action.id.trim().toLowerCase();

    if (key === 'edit' || key.startsWith('edit-') || key.endsWith('-edit')) {
      return 'edit';
    }

    if (key === 'delete' || key.startsWith('delete-') || key.endsWith('-delete')) {
      return 'delete';
    }

    return null;
  }

  trackRow(index: number, row: unknown): unknown {
    if (typeof row !== 'object' || row === null) {
      return index;
    }

    const id = (row as Record<string, unknown>)['id'];
    return id ?? index;
  }

  rowKey(row: unknown): string {
    const key = this.selectionKey();
    const value = this.valueOf(row, key);

    return value === null || value === undefined ? String(this.trackRow(0, row)) : String(value);
  }

  valueOf(row: unknown, key: string): unknown {
    if (typeof row !== 'object' || row === null) {
      return '';
    }

    return (row as Record<string, unknown>)[key];
  }

  textOf(row: unknown, column: UiTableColumn): string {
    const value = this.valueOf(row, column.key);

    if (column.formatter) {
      return column.formatter(value, row);
    }

    return value === null || value === undefined ? '' : String(value);
  }

  detailTextOf(row: unknown, field: UiTableDetailField): string {
    const value = this.valueOf(row, field.key);

    if (field.formatter) {
      return field.formatter(value, row);
    }

    return value === null || value === undefined ? '' : String(value);
  }

  detailSectionsFor(tab: UiTableDetailTab): UiTableDetailSection[] {
    if (tab.sections && tab.sections.length > 0) {
      return tab.sections;
    }

    if (tab.fields && tab.fields.length > 0) {
      return [
        {
          id: `${tab.id}-default`,
          fields: tab.fields
        }
      ];
    }

    return [];
  }

  filterValue(fieldKey: string): string | boolean {
    return this.filterDraft()[fieldKey] ?? '';
  }

  asText(value: string | boolean): string {
    return String(value);
  }

  filterAutocompleteOptions(field: UiTableFilterField): UiAutocompleteOption[] {
    return (field.options ?? []).map((option) => ({
      label: option.label,
      value: option.value,
      caption: option.caption
    }));
  }

  isActionDisabled(action: { disabled?: (row: unknown) => boolean }, row: unknown): boolean {
    return action.disabled ? action.disabled(row) : false;
  }

  isToggleChecked(column: UiTableColumn, row: unknown): boolean {
    return column.toggle?.checked ? column.toggle.checked(row) : false;
  }

  isToggleDisabled(column: UiTableColumn, row: unknown): boolean {
    return column.toggle?.disabled ? column.toggle.disabled(row) : false;
  }

  toggleAriaLabel(column: UiTableColumn, row: unknown): string {
    return column.toggle?.ariaLabel ? column.toggle.ariaLabel(row) : column.label;
  }

  activeSortKey(): string {
    const key = this.sortKey();

    if (key) {
      return key;
    }

    return this.defaultSortKey() || this.sortableColumns()[0]?.key || '';
  }

  private matchesSearch(row: unknown, query: string): boolean {
    const keys = this.searchKeys();

    if (keys.length > 0) {
      return keys.some((key) => String(this.valueOf(row, key)).toLowerCase().includes(query));
    }

    return this.columns().some((column) => String(this.valueOf(row, column.key)).toLowerCase().includes(query));
  }

  private matchesAllFilters(row: unknown, filters: Record<string, string | boolean>): boolean {
    const fields = this.filterFields();

    return fields.every((field) => {
      const filterValue = filters[field.key];

      if (field.type === 'toggle') {
        if (filterValue !== true) {
          return true;
        }

        return Boolean(this.valueOf(row, field.key));
      }

      if (filterValue === undefined || filterValue === '') {
        return true;
      }

      const rowValue = this.valueOf(row, field.key);

      if (field.type === 'text' || field.type === 'autocomplete') {
        return String(rowValue ?? '').toLowerCase().includes(String(filterValue).toLowerCase());
      }

      if (field.type === 'number') {
        return Number(rowValue) === Number(filterValue);
      }

      return String(rowValue ?? '') === String(filterValue);
    });
  }

  private normalizeBadgeKey(value: string): string {
    return value.trim().toLowerCase().replace(/\s+/g, '-');
  }

  isFirstColumn(column: UiTableColumn): boolean {
    return column.key === this.firstDataColumnKey();
  }

  shouldShowFirstColumnHover(column: UiTableColumn): boolean {
    return this.firstColumnHover() === 'hover' && this.isFirstColumn(column);
  }

  private resolveInitialPageSize(): number {
    const options = this.resolvedPageSizeOptions();

    if (options.includes(10)) {
      return 10;
    }

    const defaultSize = this.defaultPageSize();

    if (defaultSize > 0 && options.includes(defaultSize)) {
      return defaultSize;
    }

    return options[0] ?? 10;
  }

  private resolvedPageSizeOptions(): number[] {
    // Keep shared defaults strict so every list starts from a consistent page size of 10.
    const supported = new Set<number>(UiTableComponent.SUPPORTED_PAGE_SIZE_OPTIONS);
    const requested = this.pageSizeOptions().filter((value) => supported.has(value));
    const uniqueRequested = [...new Set(requested)];

    if (!uniqueRequested.includes(10)) {
      uniqueRequested.push(10);
    }

    if (uniqueRequested.length === 0) {
      return [10, 20, 50, 100];
    }

    return uniqueRequested.sort((left, right) => left - right);
  }

  private isInteractiveTarget(target: HTMLElement): boolean {
    return Boolean(
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input') ||
      target.closest('select') ||
      target.closest('textarea') ||
      target.closest('.ui-table__actions') ||
      target.closest('.ui-table__selection-cell') ||
      target.closest('.ui-table__link')
    );
  }
}
